import React from "react";
import {
  createBulkFormAction,
  type CreateBulkFormActionData,
} from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { throwIfFormServiceError } from "src/services/error";
import * as GQL from "src/types/graphql";
import type { BulkActionType } from "../activity/labels";
import type { BulkTargets } from "../types";
import { useBulkActionTargets } from "./bulkActionTargetsContext";
import { trackFormAction, trackFormActionError } from "./trackFormAction";
import { useBulkActionProgressToast } from "./useBulkActionProgressToast";
import { useRequireBulkTargets } from "./useRequireBulkTargets";

type EnqueueBulkFormArgs =
  | {
      actionType: "SET_FORM_STATUS";
      actionData: { status: GQL.form_status_enum };
    }
  | {
      actionType: "SET_FORM_VISIBILITY";
      actionData: { visibility: boolean };
    }
  | {
      actionType: "EXECUTE_FORM_RULES";
      actionData: Record<string, never>;
    };

function createBulkFormBody(
  args: EnqueueBulkFormArgs,
  targets: BulkTargets
): CreateBulkFormActionData["body"] {
  switch (args.actionType) {
    case "SET_FORM_STATUS":
      return {
        data: {
          ...targets,
          actionType: "SET_FORM_STATUS",
          actionData: args.actionData,
        },
      };
    case "SET_FORM_VISIBILITY":
      return {
        data: {
          ...targets,
          actionType: "SET_FORM_VISIBILITY",
          actionData: args.actionData,
        },
      };
    case "EXECUTE_FORM_RULES":
      return {
        data: {
          ...targets,
          actionType: "EXECUTE_FORM_RULES",
          actionData: args.actionData,
        },
      };
  }
}

export function useEnqueueBulkForm() {
  const toast = useAvelaToast();
  const showProgressToast = useBulkActionProgressToast();
  const { clearSelection, selectionSize } = useBulkActionTargets();
  const requireTargets = useRequireBulkTargets();

  return React.useCallback(
    async (args: EnqueueBulkFormArgs): Promise<boolean> => {
      try {
        const targets = await requireTargets();
        if (!targets) {
          return false;
        }

        const response = throwIfFormServiceError(
          await createBulkFormAction({
            body: createBulkFormBody(args, targets),
          })
        );

        const actionType = args.actionType as BulkActionType;
        trackFormAction({
          verb: "Queued",
          actionType,
          bulk: true,
          count: selectionSize,
          async: true,
          extra:
            args.actionType === "SET_FORM_STATUS"
              ? { status: args.actionData.status }
              : args.actionType === "SET_FORM_VISIBILITY"
              ? { visibility: args.actionData.visibility }
              : undefined,
        });

        showProgressToast({
          bulkActionId: response.data.bulkActionId,
          formTemplateId: targets.formTemplateId,
          actionType,
          selectionSize,
        });
        clearSelection();
        return true;
      } catch (err) {
        trackFormActionError(err, {
          actionType: args.actionType,
          bulk: true,
          count: selectionSize,
          async: true,
        });
        toast.error({
          title: "Oops! Something went wrong!",
          description: "Check your network and try again.",
        });
        console.error(err);
        return false;
      }
    },
    [clearSelection, requireTargets, selectionSize, showProgressToast, toast]
  );
}
